// Pan + zoom crop UI for a user-uploaded profile picture. Outputs a 512x512
// JPEG (~0.85 quality) so stored blobs are small and consistent in size.
//
// The cropping model:
//   - The viewport is a fixed square.
//   - `scale` is the multiplier on the natural image size such that
//     `scale === 1` exactly fills the viewport on the shorter axis (so the
//     image at minimum-zoom always covers the square).
//   - `offset` is the image's translation in viewport pixels relative to a
//     center-aligned default. Clamping keeps the image edges from leaving
//     the viewport.
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Loader2, X, ZoomIn, ZoomOut, Upload } from "lucide-react";
import { useProfileImageUpload } from "../lib/api/useProfileImageUpload";
import { useUpdateProfile } from "../lib/api/useProfile";
import { useToast } from "./ux/Toast";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  // Called with the new (uploaded) download URL after the crop is saved.
  onSaved?: (url: string) => void;
  // When false, the cropped image is uploaded to storage but the user's
  // profileImageUrl is NOT persisted here — the caller is responsible for
  // saving it (e.g. as part of a larger form's Save). Defaults to true.
  persist?: boolean;
}

const OUTPUT_SIZE = 512;
const JPEG_QUALITY = 0.85;
const MIN_ZOOM = 1;
const MAX_ZOOM = 4;

export function ProfileImageEditor({ isOpen, onClose, onSaved, persist = true }: Props) {
  const [file, setFile] = useState<File | null>(null);
  const [imgSrc, setImgSrc] = useState<string | null>(null);
  const [img, setImg] = useState<HTMLImageElement | null>(null);
  const [scale, setScale] = useState(MIN_ZOOM);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [viewport, setViewport] = useState(320);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const viewportRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{
    startX: number;
    startY: number;
    startOffsetX: number;
    startOffsetY: number;
  } | null>(null);
  const pinchRef = useRef<{
    startDist: number;
    startScale: number;
  } | null>(null);
  const upload = useProfileImageUpload();
  const updateProfile = useUpdateProfile();
  const toast = useToast();

  // Reset everything when the modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setFile(null);
      setImgSrc(null);
      setImg(null);
      setScale(MIN_ZOOM);
      setOffset({ x: 0, y: 0 });
    }
  }, [isOpen]);

  // Sync the on-screen viewport size to the rendered square. The viewport div
  // only mounts after an image has been picked, so we measure synchronously on
  // `img` change (before paint, to avoid a one-frame flash) and also listen for
  // window resizes.
  useLayoutEffect(() => {
    if (!isOpen || !img) return;
    const el = viewportRef.current;
    if (!el) return;
    const w = el.clientWidth;
    if (w > 0) setViewport(w);
  }, [isOpen, img]);

  useEffect(() => {
    if (!isOpen) return;
    const update = () => {
      if (viewportRef.current) {
        const w = viewportRef.current.clientWidth;
        if (w > 0) setViewport(w);
      }
    };
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, [isOpen]);

  // Decode the picked file into an HTMLImageElement.
  useEffect(() => {
    if (!file) return;
    const url = URL.createObjectURL(file);
    setImgSrc(url);
    const el = new Image();
    el.onload = () => {
      setImg(el);
      setScale(MIN_ZOOM);
      setOffset({ x: 0, y: 0 });
    };
    el.src = url;
    return () => URL.revokeObjectURL(url);
  }, [file]);

  // Escape closes the dialog.
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  // Drawn image dimensions at scale === 1 (cover-fit shorter axis).
  const baseFit = useMemo(() => {
    if (!img) return { w: 0, h: 0 };
    const ratio = img.naturalWidth / img.naturalHeight;
    if (ratio >= 1) {
      return { w: viewport * ratio, h: viewport };
    }
    return { w: viewport, h: viewport / ratio };
  }, [img, viewport]);

  // Clamp the offset so the image always covers the avatar circle (which, for
  // an inscribed circle in a square viewport, is the same per-axis constraint
  // as keeping the image edges from leaving the viewport).
  const clamp = (raw: { x: number; y: number }, s: number) => {
    const w = baseFit.w * s;
    const h = baseFit.h * s;
    const maxX = Math.max(0, (w - viewport) / 2);
    const maxY = Math.max(0, (h - viewport) / 2);
    return {
      x: Math.min(maxX, Math.max(-maxX, raw.x)),
      y: Math.min(maxY, Math.max(-maxY, raw.y)),
    };
  };

  const setScaleClamped = (next: number, pivotX?: number, pivotY?: number) => {
    const newScale = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, next));
    // Keep the pivot point stable: offset must scale by newScale/scale.
    if (pivotX != null && pivotY != null) {
      const k = newScale / scale;
      setOffset(clamp({ x: offset.x * k, y: offset.y * k }, newScale));
    } else {
      setOffset((o) => clamp(o, newScale));
    }
    setScale(newScale);
  };

  const onPointerDown = (e: React.PointerEvent) => {
    if (!img) return;
    (e.currentTarget as Element).setPointerCapture(e.pointerId);
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      startOffsetX: offset.x,
      startOffsetY: offset.y,
    };
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (!dragRef.current) return;
    const dx = e.clientX - dragRef.current.startX;
    const dy = e.clientY - dragRef.current.startY;
    setOffset(
      clamp(
        {
          x: dragRef.current.startOffsetX + dx,
          y: dragRef.current.startOffsetY + dy,
        },
        scale,
      ),
    );
  };

  const onPointerUp = () => {
    dragRef.current = null;
  };

  const onTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      pinchRef.current = {
        startDist: Math.hypot(dx, dy),
        startScale: scale,
      };
    }
  };

  const onTouchMove = (e: React.TouchEvent) => {
    if (pinchRef.current && e.touches.length === 2) {
      e.preventDefault();
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      const dist = Math.hypot(dx, dy);
      const next =
        pinchRef.current.startScale * (dist / pinchRef.current.startDist);
      setScaleClamped(next);
    }
  };

  const onTouchEnd = () => {
    pinchRef.current = null;
  };

  const onWheel = (e: React.WheelEvent) => {
    if (!img) return;
    e.preventDefault();
    const delta = -e.deltaY * 0.0015;
    setScaleClamped(scale * (1 + delta));
  };

  const handlePick = () => fileInputRef.current?.click();

  const onFileChosen = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/^image\//.test(f.type)) {
      toast.error("That file isn't an image");
      return;
    }
    if (f.size > 20 * 1024 * 1024) {
      toast.error("Image is too large — keep it under 20 MB");
      return;
    }
    setFile(f);
  };

  // Produce a 512x512 JPEG matching the current crop framing.
  const renderCrop = async (): Promise<Blob> => {
    if (!img) throw new Error("No image loaded");
    // We mirror the on-screen layout into a 512px canvas:
    //   - baseFit.{w,h} maps to OUTPUT_SIZE on the shorter axis at scale 1
    //   - offset is in viewport pixels; convert to canvas pixels via ratio
    const ratio = OUTPUT_SIZE / viewport;
    const canvas = document.createElement("canvas");
    canvas.width = OUTPUT_SIZE;
    canvas.height = OUTPUT_SIZE;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("No 2D context");
    ctx.fillStyle = "#000";
    ctx.fillRect(0, 0, OUTPUT_SIZE, OUTPUT_SIZE);
    ctx.imageSmoothingQuality = "high";
    const drawW = baseFit.w * scale * ratio;
    const drawH = baseFit.h * scale * ratio;
    const drawX = (OUTPUT_SIZE - drawW) / 2 + offset.x * ratio;
    const drawY = (OUTPUT_SIZE - drawH) / 2 + offset.y * ratio;
    ctx.drawImage(img, drawX, drawY, drawW, drawH);
    return await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (b) => (b ? resolve(b) : reject(new Error("Failed to encode JPEG"))),
        "image/jpeg",
        JPEG_QUALITY,
      );
    });
  };

  const handleSave = async () => {
    if (!img) return;
    setSaving(true);
    try {
      const blob = await renderCrop();
      const url = await upload.mutateAsync(blob);
      if (persist) {
        const res = await updateProfile.mutateAsync({ profileImageUrl: url });
        if (!res.success) {
          throw new Error(res.error || "Couldn't save profile picture");
        }
        toast.success("Profile picture updated");
      }
      onSaved?.(url);
      onClose();
    } catch (err) {
      toast.error((err as Error).message || "Couldn't save profile picture");
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal modal-open z-[9999]" onClick={onClose}>
      <motion.div
        initial={{ opacity: 0, scale: 0.97, y: 8 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.15 }}
        className="modal-box card max-w-md w-full max-h-[90dvh] flex flex-col !p-0 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="px-5 pt-5 pb-3 flex items-center gap-3 flex-shrink-0">
          <h2 className="text-lg font-semibold flex-1">Profile picture</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-200"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </header>

        <div className="flex-1 min-h-0 overflow-y-auto px-5 pb-5 space-y-4">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            onChange={onFileChosen}
          />

          {!img ? (
            <button
              type="button"
              onClick={handlePick}
              className="w-full aspect-square rounded-2xl border-2 border-dashed border-neutral-300 dark:border-neutral-700 flex flex-col items-center justify-center gap-3 hover:border-primary-500 transition-colors bg-gradient-to-br from-primary-500/10 via-secondary-500/5 to-accent-500/10"
            >
              <Upload className="w-8 h-8 text-neutral-500" />
              <div className="text-sm font-medium">Choose a photo</div>
              <div className="text-xs text-neutral-500">JPG, PNG, or WebP</div>
            </button>
          ) : (
            <>
              <div
                ref={viewportRef}
                className="relative w-full aspect-square overflow-hidden rounded-2xl bg-black select-none touch-none"
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerCancel={onPointerUp}
                onTouchStart={onTouchStart}
                onTouchMove={onTouchMove}
                onTouchEnd={onTouchEnd}
                onWheel={onWheel}
                style={{ cursor: dragRef.current ? "grabbing" : "grab" }}
              >
                {imgSrc && (
                  <img
                    src={imgSrc}
                    alt=""
                    draggable={false}
                    className="absolute pointer-events-none"
                    style={{
                      width: baseFit.w * scale,
                      height: baseFit.h * scale,
                      left: "50%",
                      top: "50%",
                      transform: `translate(calc(-50% + ${offset.x}px), calc(-50% + ${offset.y}px))`,
                      maxWidth: "none",
                    }}
                  />
                )}
                {/* Circular cutout overlay sized to the inscribed circle (the
                    real avatar shape) so the preview matches the saved crop. */}
                <div className="absolute inset-0 pointer-events-none rounded-2xl ring-1 ring-white/10" />
                <div
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    background:
                      "radial-gradient(circle closest-side at center, transparent 99%, rgba(0,0,0,0.45) 100%)",
                  }}
                />
              </div>

              <div className="flex items-center gap-3">
                <ZoomOut className="w-4 h-4 text-neutral-500" />
                <input
                  type="range"
                  min={MIN_ZOOM}
                  max={MAX_ZOOM}
                  step={0.01}
                  value={scale}
                  onChange={(e) => setScaleClamped(Number(e.target.value))}
                  className="flex-1 accent-primary-500"
                />
                <ZoomIn className="w-4 h-4 text-neutral-500" />
              </div>

              <button
                type="button"
                onClick={handlePick}
                className="text-sm primary-link"
              >
                Choose a different photo
              </button>
            </>
          )}
        </div>

        <footer className="px-5 py-4 border-t border-black/10 dark:border-white/10 flex items-center gap-2 flex-shrink-0">
          <button type="button" onClick={onClose} className="btn btn-secondary">
            Cancel
          </button>
          <button
            type="button"
            disabled={!img || saving}
            onClick={handleSave}
            className="btn btn-primary flex-1 flex items-center justify-center gap-2"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            Save
          </button>
        </footer>
      </motion.div>
    </div>
  );
}
